/**
 * Phase 1 channel catalogue — hardcoded per Q-C10 resolution.
 *
 * The Gateway does not yet deliver a channel list in `HandshakeAck.client_config`
 * (that arrives in Phase 5 platform work, CLIENT_DESIGN §22). Until then, the
 * Phase 1 server ships a fixed set of four channels and the client renders
 * them from this constant. Per-channel membership = "all connected players in
 * this world" derived from local presence state — no server API needed.
 */

export type ChannelKind =
  | 'text-placeholder' // read-only in Phase 1 — "Messages coming soon"
  | 'bridge' // `#world-chat` — bridges zone chat ↔ shell
  | 'world'; // the PixiJS world-canvas entry

export interface Channel {
  /** Stable id used as the zustand `activeChannel` key. */
  id: string;
  /** Display name, including any leading sigil (`#`, 🌍). */
  label: string;
  kind: ChannelKind;
  /**
   * Optional descriptor shown under the channel name on hover or in
   * empty-state panels. CLIENT_DESIGN §11.3.
   */
  description?: string;
}

/** Phase 1 channel list (resolution Q-C10). Order is render order. */
export const CLIENT_CHANNELS: readonly Channel[] = [
  {
    id: 'general',
    label: '# general',
    kind: 'text-placeholder',
    description:
      'Read-only in Phase 1. Text-channel messaging lands in Phase 2+.',
  },
  {
    id: 'random',
    label: '# random',
    kind: 'text-placeholder',
    description:
      'Read-only in Phase 1. Text-channel messaging lands in Phase 2+.',
  },
  {
    id: 'world-chat',
    label: '# world-chat',
    kind: 'bridge',
    description:
      'Bridges to in-world zone chat: typing here speaks as your avatar.',
  },
  {
    id: 'world',
    label: '🌍 World',
    kind: 'world',
    description: 'The isometric world canvas.',
  },
];

/** The channel the shell opens in on first load. */
export const DEFAULT_CHANNEL_ID = 'world';

export function findChannel(id: string): Channel | undefined {
  return CLIENT_CHANNELS.find((c) => c.id === id);
}
